import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
} from "recharts";
import {
  FileVideo, Share2, Users, Megaphone, RefreshCw, Eye, Trash2, Ban, Search, Send, Wallet, BarChart3,
  MessageSquare, LayoutDashboard, DollarSign, Banknote, Clock, CheckCircle2, XCircle, ShieldCheck, ArrowUpRight, ArrowDownRight,
  Sparkles, ClipboardCheck, AlertTriangle, Bot, Flag, Check, ExternalLink,
} from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { RejectClipDialog } from "@/components/clip/RejectClipDialog";
import { AppealThreadDialog } from "@/components/clip/AppealThreadDialog";

const PIE_COLORS = ["#22C55E", "#FBBF24", "#EF4444", "#8B5CF6", "#3B82F6", "#EC4899"];

function compactNum(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return String(Math.round(v));
}

function StatusBadge({ status }) {
  const map = { pending: "bg-amber-500/15 text-amber-300", accepted: "bg-emerald-500/15 text-emerald-300", flagged: "bg-rose-500/15 text-rose-300", paid: "bg-cyan-500/15 text-cyan-300", approved: "bg-emerald-500/15 text-emerald-300", rejected: "bg-rose-500/15 text-rose-300", new: "bg-violet-500/15 text-violet-300", read: "bg-amber-500/15 text-amber-300", resolved: "bg-emerald-500/15 text-emerald-300" };
  return <Badge className={`${map[status] || "bg-muted text-foreground"} hover:opacity-80 text-[10px] capitalize`}>{status}</Badge>;
}

const ROLE_OPTIONS = ["admin", "mod", "campaign_manager", "analyst", "clipper"];
const ROLE_LABELS = {
  admin: "Admin",
  mod: "Moderator",
  campaign_manager: "Campaign Manager",
  analyst: "Analyst",
  clipper: "Clipper",
};
const PERMISSION_OPTIONS = ["view_dashboard", "review_submissions", "create_campaigns", "manage_campaigns", "view_analytics", "manage_payouts", "view_creator_insights", "ban_users", "force_logout", "campaign_management_reports", "manage_roles"];
const PAGE_PERMISSION_OPTIONS = [
  "dashboard",
  "submissions",
  "socials",
  "users",
  "wallet",
  "payouts",
  "analytics",
  "roles",
  "contact",
  "announcements",
  "campaign_management",
  "mod_dashboard",
  "mod_submissions",
  "mod_socials",
  "mod_users",
  "mod_wallet",
  "mod_templates",
  "mod_bulk",
  "mod_announcements",
];

function roleLabel(name) {
  if (!name) return "";
  return ROLE_LABELS[name] || name.replace(/_/g, " ");
}

function friendlyTime(timestamp) {
  if (!timestamp) return "—";
  const t = new Date(timestamp).getTime();
  if (!t) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} Min Ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} ${h === 1 ? "Hour" : "Hours"} Ago`;
  }
  if (diff < 86400 * 30) {
    const d = Math.floor(diff / 86400);
    return `${d} ${d === 1 ? "Day" : "Days"} Ago`;
  }
  if (diff < 86400 * 365) {
    const mo = Math.floor(diff / (86400 * 30));
    return `${mo} ${mo === 1 ? "Month" : "Months"} Ago`;
  }
  const y = Math.floor(diff / (86400 * 365));
  return `${y} ${y === 1 ? "Year" : "Years"} Ago`;
}

function humanizeLastAction(action) {
  if (!action) return "—";
  // Strip "/api/" or "api/" prefix and convert "campaigns" → "Campaigns", "user/me" → "User Me"
  return String(action)
    .replace(/^\/?api\//i, "")
    .replace(/^\/+/, "")
    .split(/[\/_-]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ") || "—";
}

function activityStatus(user) {
  if (!user.last_seen_at) return { label: "Offline", color: "text-rose-400", dot: "bg-rose-400 animate-pulse", detail: "Offline" };
  const mins = Math.floor((Date.now() - new Date(user.last_seen_at).getTime()) / 60000);
  const friendly = friendlyTime(user.last_seen_at);
  if (mins <= 5) return { label: "Active", color: "text-emerald-400", dot: "bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.7)]", detail: "Active now" };
  if (mins <= 15) return { label: "Idle", color: "text-amber-400", dot: "bg-amber-400", detail: `Active ${friendly}` };
  return { label: "Offline", color: "text-rose-400", dot: "bg-rose-400", detail: `Active ${friendly}` };
}


function AdminLineSvg({ data = [], valueKey = "value", secondKey, testId, primary = "#c8ff00", secondary = "#22C55E" }) {
  const rows = data.length ? data : [];
  const allValues = rows.flatMap((row) => [Number(row[valueKey] || 0), secondKey ? Number(row[secondKey] || 0) : 0]);
  const max = Math.max(...allValues, 1);
  const denom = Math.max(rows.length - 1, 1);
  const buildPoints = (key) => rows.map((row, index) => {
    const x = (index / denom) * 100;
    const y = 88 - (Number(row[key] || 0) / max) * 68;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const primaryPoints = buildPoints(valueKey);
  const secondaryPoints = secondKey ? buildPoints(secondKey) : "";
  return (
    <div data-testid={testId} className="relative h-full min-h-[220px] overflow-hidden rounded-xl border border-border/60 bg-background p-3">
      {rows.length ? (
        <>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
            <polyline points={primaryPoints} fill="none" stroke={primary} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            {secondKey && <polyline points={secondaryPoints} fill="none" stroke={secondary} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
          </svg>
          <div className="pointer-events-none absolute inset-x-3 bottom-2 flex justify-between text-[10px] text-muted-foreground">
            {rows.slice(0, 7).map((row, index) => <span key={`${row.date}-${index}`}>{String(row.date || "").slice(5) || row.date}</span>)}
          </div>
        </>
      ) : <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data yet</div>}
    </div>
  );
}

function AdminStatusBreakdown({ items = [] }) {
  const total = Math.max(items.reduce((sum, item) => sum + Number(item.value || 0), 0), 1);
  if (!items.length) return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data</div>;
  return (
    <div data-testid="admin-status-breakdown" className="flex h-full flex-col justify-center gap-3 rounded-xl border border-border/60 bg-background p-4">
      {items.map((item, index) => {
        const pct = Math.round((Number(item.value || 0) / total) * 100);
        return (
          <div key={item.name} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="capitalize text-muted-foreground">{item.name}</span>
              <span className="font-bold tabular-nums text-foreground">{item.value} · {pct}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdminPieSvg({ data = [], testId }) {
  const total = data.reduce((sum, item) => sum + Number(item.value || 0), 0);
  if (!data.length || total <= 0) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data</div>;
  }
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  return (
    <div data-testid={testId} className="flex h-full flex-col items-center justify-center gap-4 rounded-xl border border-border/60 bg-background p-4">
      <svg viewBox="0 0 120 120" className="h-32 w-32">
        {data.map((item, index) => {
          const value = Number(item.value || 0);
          const fraction = value / total;
          const dash = fraction * circumference;
          const offset = cumulative * circumference;
          cumulative += fraction;
          return (
            <circle
              key={item.name}
              cx="60"
              cy="60"
              r={radius}
              fill="transparent"
              stroke={PIE_COLORS[index % PIE_COLORS.length]}
              strokeWidth="12"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      <div className="flex flex-wrap items-center justify-center gap-2 text-[10px] text-muted-foreground">
        {data.map((item, index) => (
          <span key={item.name} className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
            {item.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function UserAdminCard({ user, onView, onBan, busy }) {
  const status = activityStatus(user);
  const roles = user.roles || [user.role || "clipper"];
  return (
    <div data-testid={`user-row-${user.id}`} className="rounded-2xl border border-border/60 bg-background p-4">
      <div className="flex flex-wrap items-start gap-4">
        <UserAvatar user={user} size={48} testId={`admin-user-avatar-${user.id}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-bold truncate">{user.discord_username}</div>
            <span className={`inline-flex items-center gap-1 text-xs ${status.color}`}><span className={`h-2 w-2 rounded-full ${status.dot}`} />{status.label}</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{user.email || "—"} · {status.detail} · Last Action: {humanizeLastAction(user.last_action)}</div>
          <div className="mt-2 flex flex-wrap gap-1">{roles.map((r) => <Badge key={r} className="bg-[#c8ff00]/10 text-[#c8ff00] capitalize">{r.replace(/_/g," ")}</Badge>)}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="secondary" className="rounded-full" onClick={() => onView(user)}><Eye size={13}/> View User</Button>
          {user.banned ? (
            <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => onBan(user.id, false)} disabled={busy}>Unban</Button>
          ) : (
            <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => onBan(user.id, true)} disabled={busy}><Ban size={13}/></Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminPage() {
  const { me, hasAccess } = useAuth();
  const navigate = useNavigate();
  const { adminTab } = useParams();
  const adminTabAccess = {
    dashboard: { pages: ["dashboard"], permissions: ["view_dashboard", "view_analytics"] },
    submissions: { pages: ["submissions"], permissions: ["review_submissions"] },
    socials: { pages: ["socials"], permissions: ["review_submissions"] },
    users: { pages: ["users"], permissions: ["review_submissions", "view_creator_insights"] },
    wallet: { pages: ["wallet"], permissions: ["manage_payouts"] },
    payouts: { pages: ["payouts"], permissions: ["manage_payouts"] },
    analytics: { pages: ["analytics"], permissions: ["view_analytics"] },
    roles: { pages: ["roles"], permissions: ["manage_roles"] },
    contact: { pages: ["contact"], permissions: ["review_submissions"] },
    announcements: { pages: ["announcements"], permissions: ["review_submissions"] },
    bulk: { pages: ["submissions", "mod_bulk"], permissions: ["review_submissions"] },
    templates: { pages: ["submissions", "mod_templates"], permissions: ["review_submissions"] },
    appeals: { pages: ["appeals", "mod_appeals"], permissions: ["manage_appeals", "review_submissions"] },
    bots: { pages: ["bots", "mod_bots"], permissions: ["manage_bots", "review_submissions"] },
    issues: { pages: ["submissions", "payouts", "analytics"], permissions: ["review_submissions", "manage_payouts", "view_analytics"] },
  };
  const validTabs = ["dashboard", "submissions", "bulk", "templates", "appeals", "bots", "socials", "users", "wallet", "payouts", "analytics", "roles", "contact", "announcements", "issues"];
  const [tab, setTab] = useState(validTabs.includes(adminTab) ? adminTab : "dashboard");
  const accessConfig = adminTabAccess[tab];
  const canAccessTab = accessConfig ? hasAccess(accessConfig.pages, accessConfig.permissions) : false;
  const canAccessDashboard = hasAccess(["dashboard"], ["view_dashboard", "view_analytics"]);

  const [dashStats, setDashStats] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  const [subStatus, setSubStatus] = useState("pending");
  const [subSearch, setSubSearch] = useState("");
  const [submissions, setSubmissions] = useState([]);

  const [socialStatus, setSocialStatus] = useState("pending");
  const [socials, setSocials] = useState([]);

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  const [walletUserId, setWalletUserId] = useState("");
  const [walletType, setWalletType] = useState("deposit");
  const [walletAmount, setWalletAmount] = useState("");
  const [walletNote, setWalletNote] = useState("");

  const [analyticsCampaignId, setAnalyticsCampaignId] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [campaignsList, setCampaignsList] = useState([]);

  const [rolesList, setRolesList] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [roleDraft, setRoleDraft] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRoleDraft, setNewRoleDraft] = useState({ name: "", description: "", permissions: [], pages: [], status: "active", notes: "" });

  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutConfig, setPayoutConfig] = useState({ platform_fee: 1.0, min_withdrawal: 10.0, processors: { paypal: 1.03, cashapp: 0.5, crypto: 2.0 } });
  const [siteIssues, setSiteIssues] = useState([]);

  const [annTitle, setAnnTitle] = useState("");
  const [annMsg, setAnnMsg] = useState("");
  const [annScope, setAnnScope] = useState("all");
  const [annType, setAnnType] = useState("campaign");
  const [annUsers, setAnnUsers] = useState([]);
  const [annCampaignId, setAnnCampaignId] = useState("");
  const [annKind, setAnnKind] = useState("user"); // user | global | campaign
  const [announcements, setAnnouncements] = useState([]);
  const [contactFilter, setContactFilter] = useState("forms");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);


  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Wave 4 — Bulk review, Templates, Appeals, Bots
  const [selectedSubIds, setSelectedSubIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplTitle, setTplTitle] = useState("");
  const [tplReason, setTplReason] = useState("");
  const [tplMessage, setTplMessage] = useState("");
  const [appeals, setAppeals] = useState([]);
  const [appealStatus, setAppealStatus] = useState("pending");
  const [appealResponse, setAppealResponse] = useState({});
  const [appealThread, setAppealThread] = useState(null); // {submission, appeal} for the thread dialog (Submissions tab)
  const [bots, setBots] = useState([]);
  const [botInsights, setBotInsights] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null); // {id, video_url, ...}
  const [pendingBulkAfterReject, setPendingBulkAfterReject] = useState(false);
  const [userInsightAssignments, setUserInsightAssignments] = useState({}); // { userId: [campaign_ids] }
  const [insightBusyUser, setInsightBusyUser] = useState(null);

  /* Loaders */
  async function loadDash() { setDashLoading(true); try { const r = await api.get("/admin/dashboard_stats"); setDashStats(r.data); } catch {} finally { setDashLoading(false); } }
  async function loadSubmissions() { try { const r = await api.get("/admin/submissions", { params: { status: subStatus, search: subSearch || undefined } }); setSubmissions(r.data?.items || []); } catch {} }
  async function loadSocials(nextStatus = socialStatus) { try { const r = await api.get("/admin/social_requests", { params: { status: nextStatus } }); setSocials(r.data?.items || []); } catch {} }
  async function loadUsers() { try { const r = await api.get("/admin/users"); setUsers(r.data?.items || []); } catch {} }
  async function loadCampaignsList() { try { const r = await api.get("/campaigns"); setCampaignsList(r.data || []); } catch {} }
  async function loadRoles() {
    setRolesLoading(true);
    try {
      const r = await api.get("/admin/roles");
      const items = r.data?.items || [];
      setRolesList(items);
      if (!activeRoleId && items.length) {
        setActiveRoleId(items[0].id);
        setRoleDraft({ ...items[0] });
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setRolesLoading(false);
    }
  }
  async function saveRoleDraft() {
    if (!roleDraft?.id) return;
    try {
      await api.put(`/admin/roles/${roleDraft.id}`, {
        name: roleDraft.name,
        description: roleDraft.description || "",
        permissions: roleDraft.permissions || [],
        pages: roleDraft.pages || [],
        status: roleDraft.status || "active",
        notes: roleDraft.notes || "",
      });
      toast({ title: "Role updated" });
      await loadRoles();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }
  async function createRole() {
    try {
      await api.post("/admin/roles", {
        name: newRoleDraft.name,
        description: newRoleDraft.description || "",
        permissions: newRoleDraft.permissions || [],
        pages: newRoleDraft.pages || [],
        status: newRoleDraft.status || "active",
        notes: newRoleDraft.notes || "",
      });
      toast({ title: "Role created" });
      setNewRoleDraft({ name: "", description: "", permissions: [], pages: [], status: "active", notes: "" });
      setRoleDialogOpen(false);
      await loadRoles();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }
  async function deleteRole(roleId) {
    if (!window.confirm("Delete this role?")) return;
    try {
      await api.delete(`/admin/roles/${roleId}`);
      toast({ title: "Role deleted" });
      await loadRoles();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }
  async function assignRole(roleId, userId, assign) {
    try {
      await api.post(`/admin/roles/${roleId}/assign`, { user_id: userId, assign });
      await loadRoles();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }
  async function loadCampaignAnalytics(cid) { if (!cid) return; setAnalyticsLoading(true); setAnalyticsData(null); try { const r = await api.get(`/admin/campaigns/${cid}/analytics`); setAnalyticsData(r.data); } catch (e) { setError(apiErrorMessage(e)); } finally { setAnalyticsLoading(false); } }
  async function loadContact() { try { const r = await api.get("/admin/contact_submissions"); setContactSubmissions(r.data?.items || []); } catch {} }
  async function loadPayouts() { try { const r = await api.get("/admin/payout_requests"); setPayoutRequests(r.data?.items || []); try { const cfg = await api.get("/admin/payout_config"); if (cfg.data) setPayoutConfig({ platform_fee: Number(cfg.data.platform_fee ?? 1.0), min_withdrawal: Number(cfg.data.min_withdrawal ?? 10.0), processors: cfg.data.processors || {} }); } catch {} } catch {} }
  async function loadAnnouncements() { try { const r = await api.get("/admin/announcements"); setAnnouncements(r.data?.items || []); } catch {} }
  async function loadTemplates() { try { const r = await api.get("/admin/rejection_templates"); setTemplates(r.data?.items || r.data || []); } catch {} }
  async function loadAppeals() { try { const r = await api.get("/admin/appeals", { params: { status: appealStatus } }); setAppeals(Array.isArray(r.data) ? r.data : (r.data?.items || [])); } catch {} }
  async function loadBots() { try { const [r, ins] = await Promise.all([api.get("/admin/bots"), api.get("/admin/bots/insights").catch(() => ({ data: null }))]); setBots(Array.isArray(r.data) ? r.data : (r.data?.items || [])); setBotInsights(ins.data || null); } catch {} }

  async function createTemplate() {
    if (!tplTitle.trim() || !tplReason.trim()) return;
    try {
      await api.post("/admin/rejection_templates", { title: tplTitle.trim(), primary_reason: tplReason.trim(), message: tplMessage.trim(), secondary_reasons: [] });
      setTplTitle(""); setTplReason(""); setTplMessage("");
      await loadTemplates();
      toast({ title: "Template saved" });
    } catch (e) { setError(apiErrorMessage(e)); }
  }
  async function deleteTemplate(id) {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/admin/rejection_templates/${id}`); await loadTemplates(); }
    catch (e) { setError(apiErrorMessage(e)); }
  }

  async function bulkUpdateStatus(status) {
    if (!selectedSubIds.length) return;
    if (status === "flagged") {
      // Force reason via dialog
      setRejectTarget({ id: selectedSubIds[0], bulk: true, count: selectedSubIds.length });
      setPendingBulkAfterReject(true);
      return;
    }
    setBulkBusy(true);
    try {
      await Promise.all(selectedSubIds.map((id) => api.put(`/admin/submissions/${id}/status`, { status })));
      setSelectedSubIds([]);
      await loadSubmissions();
      toast({ title: "Bulk action complete", description: `${selectedSubIds.length} updated.` });
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setBulkBusy(false); }
  }
  async function bulkDeleteSubs() {
    if (!selectedSubIds.length || !window.confirm(`Delete ${selectedSubIds.length} submissions?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(selectedSubIds.map((id) => api.delete(`/admin/submissions/${id}`)));
      setSelectedSubIds([]);
      await loadSubmissions();
      toast({ title: "Submissions deleted" });
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setBulkBusy(false); }
  }

  async function respondAppeal(appealId, status) {
    setBusyId(appealId);
    try {
      const response = appealResponse[appealId] || "";
      await api.put(`/admin/appeals/${appealId}`, { status, response });
      setAppealResponse((prev) => { const next = { ...prev }; delete next[appealId]; return next; });
      await loadAppeals();
      toast({ title: status === "approved" ? "Appeal approved" : "Appeal denied" });
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setBusyId(null); }
  }

  async function toggleBot(botId, enabled) {
    setBusyId(botId);
    try {
      await api.put(`/admin/bots/${botId}`, { enabled });
      await loadBots();
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setBusyId(null); }
  }

  /* Actions */
  async function setSubmissionStatus(id, status, extra = {}) {
    if (status === "flagged" && !extra.reason) {
      // Open the reject dialog instead of firing immediately
      const sub = submissions.find((x) => x.id === id) || { id };
      setRejectTarget(sub);
      return;
    }
    setBusyId(id);
    try {
      const body = { status, ...(extra.reason ? { reason: extra.reason } : {}) };
      await api.put(`/admin/submissions/${id}/status`, body);
      await loadSubmissions();
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setBusyId(null); }
  }

  async function confirmReject({ reason }) {
    if (!rejectTarget?.id) return;
    setBusyId(rejectTarget.id);
    try {
      if (pendingBulkAfterReject) {
        await Promise.all(selectedSubIds.map((id) => api.put(`/admin/submissions/${id}/status`, { status: "flagged", reason })));
        setSelectedSubIds([]);
        toast({ title: "Bulk reject complete", description: `${selectedSubIds.length} clips rejected.` });
        setPendingBulkAfterReject(false);
      } else {
        await api.put(`/admin/submissions/${rejectTarget.id}/status`, { status: "flagged", reason });
        toast({ title: "Clip rejected", description: reason });
      }
      await loadSubmissions();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
      setRejectTarget(null);
    }
  }
  async function reviewSocial(id, status) { setBusyId(id); try { await api.put(`/admin/social_requests/${id}`, { status }); await loadSocials(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusyId(null); } }
  async function setBan(uid, banned) { const reason = banned ? window.prompt("Ban reason (optional)") : ""; setBusyId(uid); try { await api.put(`/admin/users/${uid}/ban`, { banned, reason: reason || null }); await loadUsers(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusyId(null); } }
  async function walletAdjust() { if (!walletUserId) return; try { await api.post(`/admin/wallet/${walletUserId}/adjust`, { type: walletType, amount: Number(walletAmount || 0), note: walletNote }); setWalletAmount(""); setWalletNote(""); await loadUsers(); toast({ title: "Wallet adjusted" }); } catch (e) { setError(apiErrorMessage(e)); } }
  async function updateContactStatus(id, status) { setBusyId(id); try { await api.put(`/admin/contact_submissions/${id}/status`, { status }); await loadContact(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusyId(null); } }
  async function deleteContact(id) { if (!window.confirm("Delete?")) return; setBusyId(id); try { await api.delete(`/admin/contact_submissions/${id}`); await loadContact(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusyId(null); } }
  async function updatePayoutStatus(id, status) { setBusyId(id); try { await api.put(`/admin/payout_requests/${id}/status`, { status }); await loadPayouts(); toast({ title: `Payout ${status}` }); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusyId(null); } }
  async function loadSiteIssues() { try { const r = await api.get("/admin/site_issues"); setSiteIssues(r.data?.metrics || []); } catch (e) { setError(apiErrorMessage(e)); } }
  async function savePayoutConfig() { try { const body = { platform_fee: Number(payoutConfig.platform_fee || 0), min_withdrawal: Number(payoutConfig.min_withdrawal || 0), processors: { paypal: Number(payoutConfig.processors?.paypal || 0), cashapp: Number(payoutConfig.processors?.cashapp || 0), crypto: Number(payoutConfig.processors?.crypto || 0) } }; const r = await api.put("/admin/payout_config", body); if (r.data?.config) setPayoutConfig({ platform_fee: Number(r.data.config.platform_fee), min_withdrawal: Number(r.data.config.min_withdrawal), processors: r.data.config.processors || {} }); toast({ title: "Payout config saved" }); } catch (e) { setError(apiErrorMessage(e)); } }
  async function sendAnnouncement() {
    try {
      let body;
      if (annKind === "campaign") {
        if (!annCampaignId) { toast({ title: "Pick a campaign", description: "Select which campaign this belongs to." }); return; }
        body = { title: annTitle, message: annMsg, scope: "campaign", campaign_id: annCampaignId, notification_type: "campaign" };
      } else if (annKind === "global") {
        body = { title: annTitle, message: annMsg, scope: "all", notification_type: annType, user_ids: [] };
      } else {
        // user notification — to selected users (or all if none chosen)
        const scope = annUsers.length ? "selected" : "all";
        body = { title: annTitle, message: annMsg, scope, notification_type: annType, user_ids: annUsers };
      }
      await api.post("/admin/announcements", body);
      setAnnTitle(""); setAnnMsg(""); setAnnUsers([]); setAnnCampaignId("");
      await loadAnnouncements();
      toast({ title: "Announcement sent" });
    } catch (e) { setError(apiErrorMessage(e)); }
  }
  async function openUserProfile(u) {
    setSelectedUser(u);
    setUserProfile(null);
    try {
      const r = await api.get(`/admin/users/${u.id}/profile`);
      setUserProfile(r.data);
      // Load insight-campaign assignments in parallel
      try {
        const ic = await api.get(`/admin/users/${u.id}/insight_campaigns`);
        setUserInsightAssignments((prev) => ({ ...prev, [u.id]: (ic.data || []).map((c) => c.id) }));
      } catch {}
      if (!campaignsList.length) loadCampaignsList();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function toggleUserInsightCampaign(userId, campaignId) {
    const current = userInsightAssignments[userId] || [];
    const next = current.includes(campaignId) ? current.filter((x) => x !== campaignId) : [...current, campaignId];
    setInsightBusyUser(userId);
    try {
      await api.post(`/admin/users/${userId}/insight_campaigns`, { campaign_ids: next });
      setUserInsightAssignments((prev) => ({ ...prev, [userId]: next }));
      toast({ title: "Insight access updated", description: `${next.length} campaign${next.length === 1 ? "" : "s"} assigned.` });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setInsightBusyUser(null);
    }
  }

  async function deleteAnnouncement(id) { if (!window.confirm("Delete?")) return; setBusyId(id); try { await api.delete(`/admin/announcements/${id}`); await loadAnnouncements(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusyId(null); } }

  const filteredUsers = useMemo(() => { const q = userSearch.trim().toLowerCase(); if (!q) return users; return users.filter((u) => String(u.discord_username || "").toLowerCase().includes(q) || String(u.email || "").toLowerCase().includes(q)); }, [users, userSearch]);
  const assignedRoleUsers = useMemo(() => {
    if (!roleDraft?.user_ids?.length) return [];
    return roleDraft.user_ids.map((id) => users.find((u) => u.id === id)).filter(Boolean);
  }, [roleDraft, users]);
  const newContactCount = contactSubmissions.filter((c) => c.status === "new").length;
  const pendingPayouts = payoutRequests.filter((p) => p.status === "pending").length;
  const filteredContacts = contactSubmissions.filter((c) => contactFilter === "bugs" ? (c.name === "FeedBacks" || String(c.subject || "").toLowerCase().includes("bug")) : !(c.name === "FeedBacks" || String(c.subject || "").toLowerCase().includes("bug")));

  useEffect(() => {
    if (validTabs.includes(adminTab)) setTab(adminTab);
    else setTab("dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab]);

  async function refresh() {
    setError(null);
    const access = adminTabAccess[tab];
    if (!access || !hasAccess(access.pages, access.permissions)) return;
    try { if (tab === "dashboard") { await loadDash(); } else if (tab === "submissions") { await loadSubmissions(); } else if (tab === "socials") { await loadSocials(); } else if (tab === "users") { await loadUsers(); } else if (tab === "wallet") { await loadUsers(); } else if (tab === "analytics") { await loadCampaignsList(); } else if (tab === "roles") { await Promise.all([loadRoles(), loadUsers()]); } else if (tab === "contact") { await loadContact(); } else if (tab === "payouts") { await loadPayouts(); } else if (tab === "announcements") { await Promise.all([loadUsers(), loadAnnouncements(), loadCampaignsList()]); } else if (tab === "templates") { await loadTemplates(); } else if (tab === "bulk") { await loadSubmissions(); } else if (tab === "appeals") { await loadAppeals(); } else if (tab === "bots") { await loadBots(); } else if (tab === "issues") { await loadSiteIssues(); } } catch (e) { setError(apiErrorMessage(e)); }
  }

  useEffect(() => { if (canAccessDashboard) { loadDash(); } }, [canAccessDashboard]);
  useEffect(() => { if (canAccessTab) refresh(); }, [canAccessTab, tab]);
  useEffect(() => { if (canAccessTab && tab === "submissions") loadSubmissions(); }, [subStatus, canAccessTab, tab]);
  useEffect(() => { if (canAccessTab && tab === "appeals") loadAppeals(); }, [appealStatus, canAccessTab, tab]);

  useEffect(() => {
    const selected = rolesList.find((role) => role.id === activeRoleId);
    if (selected) setRoleDraft({ ...selected });
  }, [activeRoleId, rolesList]);


  if (!canAccessTab) {
    return (
      <div data-testid="admin-locked" className="flex min-h-[60vh] items-center justify-center">
        <Card className="w-full max-w-md border-border/60 bg-card/70">
          <CardHeader>
            <CardTitle data-testid="admin-title" className="text-base">Admin Console</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              You don't have access to this admin page.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusDonut = dashStats?.status_counts ? Object.entries(dashStats.status_counts).map(([k, v]) => ({ name: k, value: v })) : [];
  const platformPie = dashStats?.platform_views ? dashStats.platform_views.map((p) => ({ name: p.platform, value: p.views })) : [];

  return (
    <div data-testid="admin-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 data-testid="admin-title" className="page-title">Admin Console</h1>
          <p className="page-subtitle">Manage your platform</p>
        </div>
        <Button data-testid="admin-refresh-all" variant="secondary" size="sm" className="gap-1.5 rounded-full" onClick={refresh}><RefreshCw size={13} /> Refresh</Button>
      </div>

      {error && <div data-testid="admin-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>}

      {/* Stats Cards */}
      {tab === "dashboard" && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[
          { id: "pending-subs", icon: Clock, color: "amber", label: "Pending Subs", value: dashStats?.pending_submissions, delta: dashStats?.deltas?.pending_submissions, desc: "Awaiting review" },
          { id: "pending-socials", icon: Share2, color: "violet", label: "Pending Socials", value: dashStats?.pending_socials, delta: dashStats?.deltas?.pending_socials, desc: "Verification queue" },
          { id: "total-clippers", icon: Users, color: "emerald", label: "Clippers", value: dashStats?.total_clippers, delta: dashStats?.deltas?.total_clippers, desc: "Active creators" },
          { id: "total-views", icon: Eye, color: "cyan", label: "Total Views", value: dashStats ? Number(dashStats.total_views).toLocaleString() : "—", delta: dashStats?.deltas?.total_views, desc: "All time" },
          { id: "total-subs", icon: FileVideo, color: "blue", label: "Total Subs", value: dashStats?.total_submissions, delta: dashStats?.deltas?.total_submissions, desc: "All submissions" },
          { id: "new-messages", icon: MessageSquare, color: "pink", label: "New Messages", value: dashStats?.new_messages ?? newContactCount, delta: dashStats?.deltas?.new_messages, desc: "New inquiries" },
        ].map((s) => (
          <div key={s.id} data-testid={`dash-${s.id}`} className={`rounded-2xl border border-${s.color}-500/20 bg-${s.color}-500/[0.04] p-5`}>
            <div className="flex items-center gap-1.5"><s.icon size={13} className={`text-${s.color}-400`} /><span className={`text-[10px] font-medium uppercase tracking-wider text-${s.color}-400`}>{s.label}</span></div>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-3xl font-black">{s.value ?? "—"}</span>
              <span
                data-testid={`dash-${s.id}-trend`}
                className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full ${Number(s.delta || 0) > 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}
              >
                {Number(s.delta || 0) > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              </span>
            </div>
            <div className="mt-0.5 text-[10px] text-muted-foreground">{s.desc}</div>
          </div>
        ))}
      </div>}

      {/* Tabs — vertical left rail on desktop, horizontal wrap on mobile */}
      <Tabs value={tab} onValueChange={(value) => navigate(`/admin/${value}`)} className="w-full">
        <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
          <TabsList
            data-testid="admin-tablist"
            className="flex w-full flex-row flex-wrap items-start justify-start gap-1 bg-card/60 p-1.5 h-auto lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:flex-col lg:flex-nowrap lg:overflow-y-auto lg:self-start lg:rounded-2xl"
          >
          {[
            { v: "dashboard", l: "Dashboard", i: LayoutDashboard },
            { v: "submissions", l: "Submissions", i: FileVideo },
            { v: "bulk", l: "Bulk Review", i: ClipboardCheck },
            { v: "templates", l: "Templates", i: Sparkles },
            { v: "appeals", l: "Appeals", i: AlertTriangle },
            { v: "bots", l: "Bots", i: Bot },
            { v: "socials", l: "Socials", i: Share2 },
            { v: "users", l: "Users", i: Users },
            { v: "wallet", l: "Wallet", i: Wallet },
            { v: "payouts", l: "Payouts", i: Banknote },
            { v: "analytics", l: "Analytics", i: BarChart3 },
            { v: "roles", l: "Roles", i: ShieldCheck },
            { v: "contact", l: "Contact", i: MessageSquare },
            { v: "announcements", l: "Announce", i: Megaphone },
            { v: "issues", l: "Site Issues", i: AlertTriangle },
          ].filter((t) => {
            const cfg = adminTabAccess[t.v];
            return cfg ? hasAccess(cfg.pages, cfg.permissions) : true;
          }).map(({ v, l, i: Icon }) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`admin-tab-${v}`}
              className="rounded-full px-3 py-1.5 text-xs gap-1.5 data-[state=active]:bg-[#c8ff00] data-[state=active]:text-black lg:w-full lg:justify-start lg:rounded-lg lg:px-3 lg:py-2 lg:text-[13px] lg:font-semibold"
            >
              <Icon size={12} />{l}
            </TabsTrigger>
          ))}
          </TabsList>

          <div className="min-w-0">

        {/* ── DASHBOARD ── */}
        <TabsContent value="dashboard" className="mt-4 space-y-4">
          {tab === "dashboard" && (<>
          {dashLoading && <div className="py-10 text-center text-sm text-muted-foreground">Loading dashboard...</div>}
          {dashStats && (<>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="border-border/60 bg-card/80"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Submissions Over Time</CardTitle></CardHeader><CardContent className="h-[260px]">
                <AdminLineSvg data={dashStats.timeline || []} valueKey="total" secondKey="accepted" testId="admin-dashboard-submissions-trend" primary="#8B5CF6" secondary="#22C55E" />
              </CardContent></Card>
              <Card className="border-border/60 bg-card/80"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Views Trend</CardTitle></CardHeader><CardContent className="h-[260px]">
                <AdminLineSvg data={dashStats.views_timeline || []} valueKey="views" testId="admin-dashboard-views-trend" primary="#c8ff00" />
              </CardContent></Card>
              <Card className="border-border/60 bg-card/80"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Submission Status</CardTitle></CardHeader><CardContent className="h-[260px]">
                <AdminStatusBreakdown items={statusDonut} />
              </CardContent></Card>
              <Card className="border-border/60 bg-card/80"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Platform Views</CardTitle></CardHeader><CardContent className="h-[260px]">
                <AdminPieSvg data={platformPie} testId="admin-dashboard-platform-pie" />
              </CardContent></Card>
            </div>
            {dashStats.top_clippers?.length > 0 && (<Card className="border-border/60 bg-card/80"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Top Clippers</CardTitle></CardHeader><CardContent><div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">{dashStats.top_clippers.map((c,idx)=>(<div key={c.user_id} className="flex items-center gap-2 rounded-xl border border-border/60 bg-background p-3"><div className={`flex h-6 w-6 items-center justify-center rounded-lg text-[10px] font-bold ${idx===0?"bg-amber-500/15 text-amber-400":idx===1?"bg-zinc-400/15 text-zinc-300":"bg-muted text-muted-foreground"}`}>#{idx+1}</div><div className="min-w-0 flex-1"><div className="text-xs font-medium truncate">{c.username}</div><div className="text-[10px] text-muted-foreground">{c.clips} clips · {Number(c.views).toLocaleString()} views</div></div></div>))}</div></CardContent></Card>)}
          </>)}
          </>)}
        </TabsContent>

        {/* ── SUBMISSIONS ── */}
        <TabsContent value="submissions" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={subStatus} onValueChange={setSubStatus}><SelectTrigger className="w-[130px] border-border/60 bg-background"><SelectValue/></SelectTrigger><SelectContent>{["pending","accepted","flagged","paid"].map(s=><SelectItem key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase()+s.slice(1)}</SelectItem>)}</SelectContent></Select>
              <div className="relative flex-1 min-w-[160px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/><Input placeholder="Search..." value={subSearch} onChange={e=>setSubSearch(e.target.value)} className="pl-9 border-border/60 bg-background text-sm"/></div>
              <Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={loadSubmissions}><RefreshCw size={12}/></Button>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {submissions.length===0&&<div className="py-6 text-center text-sm text-muted-foreground">No submissions</div>}
              {submissions.map(s=>(<div key={s.id} data-testid={`sub-row-${s.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-background p-3"><StatusBadge status={s.status}/><UserAvatar name={s.clipper_tag} src={s.clipper_avatar} size={32} testId={`submission-avatar-${s.id}`} /><div className="min-w-0 flex-1"><div className="text-xs font-medium truncate">{s.clipper_tag||"Unknown"}</div><div className="text-[10px] text-muted-foreground truncate">{s.campaign_name} · {s.platform}</div>{s.latest_appeal && (<button data-testid={`sub-appeal-badge-${s.id}`} type="button" onClick={()=>setAppealThread({submission:s, appeal:s.latest_appeal})} className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${s.latest_appeal.status === "approved" ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : s.latest_appeal.status === "denied" ? "bg-rose-500/15 text-rose-300 hover:bg-rose-500/25" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"}`}>⚖ Appeal · {s.latest_appeal.status}</button>)}</div><div className="text-right"><div className="text-xs font-semibold tabular-nums">{Number(s.views||0).toLocaleString()} views</div></div><div className="flex items-center gap-1 shrink-0">{s.status==="pending"&&<><Button title="Approve" size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-400 hover:bg-emerald-500/10" onClick={()=>setSubmissionStatus(s.id,"accepted")} disabled={busyId===s.id}><CheckCircle2 size={15}/></Button><Button title="Reject" size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/10" onClick={()=>setSubmissionStatus(s.id,"flagged")} disabled={busyId===s.id}><XCircle size={15}/></Button></>}{s.status==="accepted"&&<Button title="Mark Paid" size="sm" variant="ghost" className="h-7 w-7 p-0 text-cyan-400 hover:bg-cyan-500/10" onClick={()=>setSubmissionStatus(s.id,"paid")} disabled={busyId===s.id}><CheckCircle2 size={15}/></Button>}<a href={s.video_url} target="_blank" rel="noreferrer" className="inline-flex h-7 items-center rounded bg-[#c8ff00]/10 px-2 text-[10px] font-medium text-[#c8ff00] hover:bg-[#c8ff00]/20">Open</a><Button title="Delete clip" size="sm" variant="ghost" data-testid={`sub-delete-btn-${s.id}`} className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/10" onClick={async ()=>{ if(!window.confirm("Delete this clip permanently?")) return; try { await api.delete(`/admin/submissions/${s.id}`); toast({title:"Clip deleted"}); loadSubmissions(); } catch(e) { toast({title:"Delete failed", description: apiErrorMessage(e)}); } }} disabled={busyId===s.id}><Trash2 size={14}/></Button></div></div>))}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ── SOCIALS ── */}
        <TabsContent value="socials" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-2"><Select value={socialStatus} onValueChange={v=>{setSocialStatus(v);loadSocials(v);}}><SelectTrigger className="w-[130px] border-border/60 bg-background"><SelectValue/></SelectTrigger><SelectContent>{["pending","approved","rejected"].map(s=><SelectItem key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase()+s.slice(1)}</SelectItem>)}</SelectContent></Select><Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={()=>loadSocials(socialStatus)}><RefreshCw size={12}/></Button></div>
            <div className="space-y-3 max-h-[560px] overflow-y-auto">{socials.length===0&&<div className="py-6 text-center text-sm text-muted-foreground">No social requests</div>}{socials.map(s=>(<div key={s.id} className="rounded-2xl border border-border/60 bg-background p-5"><div className="flex flex-wrap items-start gap-4"><StatusBadge status={s.status}/><UserAvatar name={s.clipper_username} src={s.clipper_avatar} size={42} testId={`social-avatar-${s.id}`} /><div className="min-w-0 flex-1"><div className="text-sm font-bold">{s.platform} · @{s.handle}</div><div className="mt-1 text-xs text-muted-foreground">Creator: {s.clipper_username||"Unknown"}</div><div className="mt-2 break-all rounded-xl border border-border/50 bg-muted/20 px-3 py-2 text-xs text-foreground">{s.profile_url || s.url || s.handle || "No profile link submitted"}</div>{s.niches?.length ? <div className="mt-2 flex flex-wrap gap-1">{s.niches.map(n=><Badge key={n} className="bg-[#c8ff00]/10 text-[#c8ff00]">{n}</Badge>)}</div> : null}</div>{s.status==="pending"&&<div className="flex gap-1"><Button title="Approve" size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-400 hover:bg-emerald-500/10" onClick={()=>reviewSocial(s.id,"approved")} disabled={busyId===s.id}><CheckCircle2 size={17}/></Button><Button title="Reject" size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400 hover:bg-rose-500/10" onClick={()=>reviewSocial(s.id,"rejected")} disabled={busyId===s.id}><XCircle size={17}/></Button></div>}</div></div>))}</div>
          </CardContent></Card>
        </TabsContent>

        {/* ── USERS ── */}
        <TabsContent value="users" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-2"><div className="relative flex-1 min-w-[160px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/><Input placeholder="Search users..." value={userSearch} onChange={e=>setUserSearch(e.target.value)} className="pl-9 border-border/60 bg-background text-sm"/></div><Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={loadUsers}><RefreshCw size={12}/></Button></div>
            <div className="space-y-3 max-h-[560px] overflow-y-auto">{filteredUsers.map(u=><UserAdminCard key={u.id} user={u} onView={openUserProfile} onBan={setBan} busy={busyId===u.id} />)}</div>
          </CardContent></Card>
        </TabsContent>

        {/* ── WALLET ── */}
        <TabsContent value="wallet" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5"><div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5"><label className="text-[10px] text-muted-foreground">User</label><Select value={walletUserId} onValueChange={setWalletUserId}><SelectTrigger className="w-[180px] border-border/60 bg-background text-sm"><SelectValue placeholder="Select user"/></SelectTrigger><SelectContent>{users.map(u=><SelectItem key={u.id} value={u.id}>{u.discord_username}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid gap-1.5"><label className="text-[10px] text-muted-foreground">Type</label><Select value={walletType} onValueChange={setWalletType}><SelectTrigger className="w-[120px] border-border/60 bg-background text-sm"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="deposit">Deposit</SelectItem><SelectItem value="withdraw">Withdraw</SelectItem></SelectContent></Select></div>
            <div className="grid gap-1.5"><label className="text-[10px] text-muted-foreground">Amount</label><Input type="number" value={walletAmount} onChange={e=>setWalletAmount(e.target.value)} className="w-[100px] border-border/60 bg-background text-sm" placeholder="0.00"/></div>
            <div className="grid gap-1.5 flex-1 min-w-[120px]"><label className="text-[10px] text-muted-foreground">Note</label><Input value={walletNote} onChange={e=>setWalletNote(e.target.value)} className="border-border/60 bg-background text-sm" placeholder="Reason..."/></div>
            <Button size="sm" className="gap-1 rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={walletAdjust} disabled={!walletUserId||!walletAmount}><DollarSign size={13}/> Adjust</Button>
          </div></CardContent></Card>
        </TabsContent>

        {/* ── PAYOUTS ── */}
        <TabsContent value="payouts" className="mt-4">
          <Card data-testid="admin-payout-settings" className="mb-4 border-[#c8ff00]/20 bg-[#c8ff00]/[0.03]"><CardContent className="pt-5">
            <div className="mb-3 text-sm font-semibold text-white">Payout Configuration</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <div className="grid gap-1.5"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">Min Withdrawal ($)</label><Input data-testid="admin-min-withdrawal-input" type="number" min="0" step="0.01" value={payoutConfig.min_withdrawal} onChange={e=>setPayoutConfig(c=>({...c, min_withdrawal: e.target.value}))} className="border-[#1a1a1a] bg-black text-white" /></div>
              <div className="grid gap-1.5"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">Platform Fee ($)</label><Input data-testid="admin-platform-fee-input" type="number" min="0" step="0.01" value={payoutConfig.platform_fee} onChange={e=>setPayoutConfig(c=>({...c, platform_fee: e.target.value}))} className="border-[#1a1a1a] bg-black text-white" /></div>
              <div className="grid gap-1.5"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">PayPal Fee ($)</label><Input data-testid="admin-fee-paypal-input" type="number" min="0" step="0.01" value={payoutConfig.processors?.paypal ?? 0} onChange={e=>setPayoutConfig(c=>({...c, processors: {...c.processors, paypal: e.target.value}}))} className="border-[#1a1a1a] bg-black text-white" /></div>
              <div className="grid gap-1.5"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cash App Fee ($)</label><Input data-testid="admin-fee-cashapp-input" type="number" min="0" step="0.01" value={payoutConfig.processors?.cashapp ?? 0} onChange={e=>setPayoutConfig(c=>({...c, processors: {...c.processors, cashapp: e.target.value}}))} className="border-[#1a1a1a] bg-black text-white" /></div>
              <div className="grid gap-1.5"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">USDT Fee ($)</label><Input data-testid="admin-fee-crypto-input" type="number" min="0" step="0.01" value={payoutConfig.processors?.crypto ?? 0} onChange={e=>setPayoutConfig(c=>({...c, processors: {...c.processors, crypto: e.target.value}}))} className="border-[#1a1a1a] bg-black text-white" /></div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">Total fee = Platform Fee + Processor Fee (per method).</div>
              <Button data-testid="admin-payout-config-save" className="rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]" onClick={savePayoutConfig}>Save Config</Button>
            </div>
          </CardContent></Card>
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex items-center justify-between"><span className="text-sm font-semibold">Payout Requests</span><Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={loadPayouts}><RefreshCw size={12}/></Button></div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {payoutRequests.length===0&&<div className="py-6 text-center text-sm text-muted-foreground">No payout requests</div>}
              {payoutRequests.map(p=>(<div key={p.id} data-testid={`payout-req-${p.id}`} className="rounded-2xl border border-border/60 bg-background p-5"><div className="flex flex-wrap items-start gap-4"><StatusBadge status={p.status}/><UserAvatar name={p.clipper_name} src={p.clipper_avatar} size={42} testId={`payout-avatar-${p.id}`} /><div className="min-w-0 flex-1"><div className="text-sm font-bold">{p.clipper_name}</div><div className="mt-1 text-xs text-muted-foreground capitalize">Payment Method: {p.method}</div><div className="mt-2 break-all rounded-xl border border-border/50 bg-muted/20 px-3 py-2 text-xs text-foreground">{p.method_detail || "No payment details provided"}</div></div><div className="min-w-[160px] rounded-xl border border-border/50 bg-muted/20 p-3 text-right"><div className="text-lg font-black tabular-nums">${Number(p.amount).toFixed(2)}</div><div className="text-xs text-muted-foreground">Fee: ${Number(p.fee).toFixed(2)}</div><div className="text-xs font-bold text-[#c8ff00]">Pays: ${Number(p.amount_paid).toFixed(2)}</div><div className="mt-1 text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</div></div>{p.status==="pending"&&<div className="flex gap-1 shrink-0"><Button title="Approve" size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-400 hover:bg-emerald-500/10" onClick={()=>updatePayoutStatus(p.id,"approved")} disabled={busyId===p.id}><CheckCircle2 size={17}/></Button><Button title="Reject" size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400 hover:bg-rose-500/10" onClick={()=>updatePayoutStatus(p.id,"rejected")} disabled={busyId===p.id}><XCircle size={17}/></Button></div>}{p.status==="approved"&&<Button title="Mark Paid" size="sm" variant="ghost" className="h-8 w-8 p-0 text-cyan-400 hover:bg-cyan-500/10" onClick={()=>updatePayoutStatus(p.id,"paid")} disabled={busyId===p.id}><CheckCircle2 size={17}/></Button>}</div></div>))}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ── ANALYTICS ── */}
        <TabsContent value="analytics" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-2"><Select value={analyticsCampaignId} onValueChange={v=>{setAnalyticsCampaignId(v);loadCampaignAnalytics(v);}}><SelectTrigger className="w-[220px] border-border/60 bg-background"><SelectValue placeholder="Select campaign"/></SelectTrigger><SelectContent>{campaignsList.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>{analyticsCampaignId&&<Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={()=>window.open(`${window.location.origin}/api/admin/campaigns/${analyticsCampaignId}/export`,"_blank")}>Export CSV</Button>}</div>
            <div className="text-lg font-black">Campaigns Analytics</div>
            {analyticsLoading&&<div className="py-4 text-center text-sm text-muted-foreground">Loading...</div>}
            {analyticsData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-xl border border-border/60 bg-background p-4">
                    <div className="text-[10px] text-muted-foreground">Submissions</div>
                    <div className="mt-1 text-2xl font-bold">{analyticsData.total_submissions}</div>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-background p-4">
                    <div className="text-[10px] text-muted-foreground">Approval Rate</div>
                    <div className="mt-1 text-2xl font-bold text-[#c8ff00]">{analyticsData.approval_rate}%</div>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-background p-4">
                    <div className="text-[10px] text-muted-foreground">Total Views</div>
                    <div className="mt-1 text-2xl font-bold">{Number(analyticsData.total_views).toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-background p-4">
                    <div className="text-[10px] text-muted-foreground">Payouts</div>
                    <div className="mt-1 text-2xl font-bold text-[#c8ff00]">${analyticsData.total_earned}</div>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Views Line Trend</CardTitle></CardHeader>
                    <CardContent className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <LineChart data={analyticsData.daily_views || []}>
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} />
                          <YAxis tickFormatter={compactNum} tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} />
                          <Tooltip formatter={(value) => Number(value).toLocaleString()} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" }} />
                          <Line dataKey="views" stroke="#c8ff00" strokeWidth={3} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Submissions Trend</CardTitle></CardHeader>
                    <CardContent className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <LineChart data={analyticsData.daily_views || []}>
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} />
                          <YAxis tickFormatter={compactNum} tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} />
                          <Tooltip formatter={(value) => Number(value).toLocaleString()} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" }} />
                          <Line dataKey="submissions" stroke="#8B5CF6" strokeWidth={3} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Status Pie Graph</CardTitle></CardHeader>
                    <CardContent className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <PieChart>
                          <Pie data={analyticsData.status_breakdown || []} dataKey="value" nameKey="name" outerRadius={90}>
                            {(analyticsData.status_breakdown || []).map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Platform Breakdown</CardTitle></CardHeader>
                    <CardContent className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <BarChart data={analyticsData.platform_breakdown || []}>
                          <XAxis dataKey="platform" tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} />
                          <YAxis tickFormatter={compactNum} tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }} />
                          <Tooltip formatter={(value) => Number(value).toLocaleString()} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" }} />
                          <Bar dataKey="views" fill="#c8ff00" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
                {analyticsData.top_performers?.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-sm font-bold">Top Performers</div>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {analyticsData.top_performers.slice(0, 6).map((p, idx) => (
                        <div key={p.user_id} className="rounded-2xl border border-border/60 bg-background p-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#c8ff00] text-sm font-black text-black">#{idx + 1}</span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-bold">@{p.username}</div>
                              <div className="text-xs text-muted-foreground">Campaign performer</div>
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                            <div className="rounded-xl bg-muted/30 p-3">
                              <div className="text-xs text-muted-foreground">Views</div>
                              <div className="font-black">{Number(p.views).toLocaleString()}</div>
                            </div>
                            <div className="rounded-xl bg-[#c8ff00]/10 p-3">
                              <div className="text-xs text-muted-foreground">Earnings</div>
                              <div className="font-black text-[#c8ff00]">${p.earned}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* ── ROLES ── */}
        <TabsContent value="roles" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 data-testid="admin-roles-title" className="text-lg font-bold">Roles</h2>
              <p className="text-sm text-muted-foreground">Create roles, assign access, and manage permissions.</p>
            </div>
            <Button data-testid="admin-create-role" className="rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" onClick={() => setRoleDialogOpen(true)}>Create Role</Button>
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card className="border-border/60 bg-card/80">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                        <th className="pb-3 pr-4">Role Name</th>
                        <th className="pb-3 pr-4">Description</th>
                        <th className="pb-3 pr-4">Users</th>
                        <th className="pb-3 pr-4">Status</th>
                        <th className="pb-3 pr-4">Last Updated</th>
                        <th className="pb-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rolesLoading ? (
                        <tr><td colSpan="6" className="py-6 text-center text-sm text-muted-foreground">Loading roles...</td></tr>
                      ) : rolesList.length === 0 ? (
                        <tr><td colSpan="6" className="py-6 text-center text-sm text-muted-foreground">No roles created yet.</td></tr>
                      ) : rolesList.map((role) => (
                        <tr key={role.id} data-testid={`role-row-${role.id}`} className="border-b border-border/60">
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                              {roleLabel(role.name)}
                              {role.system && <Badge className="bg-[#c8ff00]/10 text-[#c8ff00]">System</Badge>}
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground">{role.description || "—"}</td>
                          <td className="py-3 pr-4 text-xs font-semibold">{role.user_count ?? 0}</td>
                          <td className="py-3 pr-4"><Badge className={role.status === "disabled" ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}>{role.status}</Badge></td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground">{role.updated_at ? new Date(role.updated_at).toLocaleDateString() : "—"}</td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-1">
                              <Button data-testid={`role-view-${role.id}`} size="sm" variant="secondary" onClick={() => { setActiveRoleId(role.id); setRoleDraft({ ...role }); }}>View</Button>
                              <Button data-testid={`role-edit-${role.id}`} size="sm" variant="secondary" onClick={() => { setActiveRoleId(role.id); setRoleDraft({ ...role }); }}>Edit</Button>
                              <Button data-testid={`role-delete-${role.id}`} size="sm" variant="ghost" className="text-rose-400" onClick={() => deleteRole(role.id)} disabled={role.system}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 bg-card/80">
              <CardContent className="pt-5 space-y-4">
                {roleDraft ? (
                  <div data-testid="role-details-panel" className="space-y-4">
                    <div className="grid gap-2">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground">Role Name</label>
                      <Input data-testid="role-name-input" value={roleDraft.system ? roleLabel(roleDraft.name) : (roleDraft.name || "")} onChange={(e) => { if (!roleDraft.system) setRoleDraft({ ...roleDraft, name: e.target.value }); }} className="border-border/60 bg-background text-sm" disabled={roleDraft.system} />
                    </div>
                    <div className="grid gap-2">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground">Description</label>
                      <Textarea data-testid="role-description-input" value={roleDraft.description || ""} onChange={(e) => setRoleDraft({ ...roleDraft, description: e.target.value })} className="border-border/60 bg-background text-sm" />
                    </div>
                    <div className="grid gap-2">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground">Status</label>
                      <Select value={roleDraft.status || "active"} onValueChange={(value) => setRoleDraft({ ...roleDraft, status: value })}>
                        <SelectTrigger data-testid="role-status-select" className="border-border/60 bg-background"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</label>
                      <Textarea data-testid="role-notes-input" value={roleDraft.notes || ""} onChange={(e) => setRoleDraft({ ...roleDraft, notes: e.target.value })} className="border-border/60 bg-background text-sm" placeholder="Internal notes..." />
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Permissions</div>
                      <div className="flex flex-wrap gap-2">
                        {PERMISSION_OPTIONS.map((perm) => (
                          <Button
                            key={perm}
                            data-testid={`role-permission-${perm}`}
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={(roleDraft.permissions || []).includes(perm) ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full"}
                            onClick={() => {
                              const perms = roleDraft.permissions || [];
                              setRoleDraft({ ...roleDraft, permissions: perms.includes(perm) ? perms.filter((p) => p !== perm) : [...perms, perm] });
                            }}
                          >
                            {perm.replace(/_/g, " ")}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Assigned Pages</div>
                      <div className="flex flex-wrap gap-2">
                        {PAGE_PERMISSION_OPTIONS.map((page) => (
                          <Button
                            key={page}
                            data-testid={`role-page-${page}`}
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={(roleDraft.pages || []).includes(page) ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full"}
                            onClick={() => {
                              const pages = roleDraft.pages || [];
                              setRoleDraft({ ...roleDraft, pages: pages.includes(page) ? pages.filter((p) => p !== page) : [...pages, page] });
                            }}
                          >
                            {page.replace(/_/g, " ")}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Assigned Users</div>
                      <div className="flex flex-wrap gap-2">
                        {assignedRoleUsers.length === 0 ? (
                          <span className="text-xs text-muted-foreground">No users assigned yet.</span>
                        ) : assignedRoleUsers.map((u) => (
                          <span key={u.id} className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background px-2 py-1 text-xs">
                            <UserAvatar user={u} size={22} />
                            @{u.discord_username || u.username || "Unknown"}
                            <Button
                              data-testid={`role-remove-user-${u.id}`}
                              size="sm"
                              variant="ghost"
                              className="h-5 w-5 p-0 text-rose-400"
                              onClick={() => assignRole(roleDraft.id, u.id, false)}
                            >
                              <XCircle size={12} />
                            </Button>
                          </span>
                        ))}
                      </div>
                      <Select onValueChange={(value) => assignRole(roleDraft.id, value, true)}>
                        <SelectTrigger data-testid="role-assign-user-select" className="border-border/60 bg-background"><SelectValue placeholder="Assign user" /></SelectTrigger>
                        <SelectContent>
                          {users.filter((u) => !(roleDraft.user_ids || []).includes(u.id)).map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.discord_username || u.username || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button data-testid="role-save-button" className="w-full rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" onClick={saveRoleDraft}>Save Role</Button>
                  </div>
                ) : (
                  <div className="py-10 text-center text-sm text-muted-foreground">Select a role to view details.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── BULK REVIEW ── */}
        <TabsContent value="bulk" className="mt-4">
          <Card data-testid="admin-bulk-page" className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={subStatus} onValueChange={setSubStatus}><SelectTrigger className="w-[130px] border-border/60 bg-background"><SelectValue/></SelectTrigger><SelectContent>{["pending","accepted","flagged","paid"].map(s=><SelectItem key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase()+s.slice(1)}</SelectItem>)}</SelectContent></Select>
              <Button data-testid="admin-bulk-accept" size="sm" className="rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/20" disabled={!selectedSubIds.length || bulkBusy} onClick={() => bulkUpdateStatus("accepted")}><Check size={13}/> Accept</Button>
              <Button data-testid="admin-bulk-flag" size="sm" variant="secondary" className="rounded-full bg-amber-500/15 text-amber-300 hover:bg-amber-500/20" disabled={!selectedSubIds.length || bulkBusy} onClick={() => bulkUpdateStatus("flagged")}><Flag size={13}/> Flag</Button>
              <Button data-testid="admin-bulk-mark-paid" size="sm" variant="secondary" className="rounded-full bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/20" disabled={!selectedSubIds.length || bulkBusy} onClick={() => bulkUpdateStatus("paid")}><DollarSign size={13}/> Mark Paid</Button>
              <Button data-testid="admin-bulk-delete" size="sm" variant="secondary" className="rounded-full bg-rose-500/15 text-rose-300 hover:bg-rose-500/20" disabled={!selectedSubIds.length || bulkBusy} onClick={bulkDeleteSubs}><Trash2 size={13}/> Delete</Button>
              <div data-testid="admin-bulk-selected-count" className="ml-auto text-xs text-muted-foreground">{selectedSubIds.length} selected</div>
              <Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={loadSubmissions}><RefreshCw size={12}/></Button>
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto">
              {submissions.length === 0 && <div data-testid="admin-bulk-empty" className="py-6 text-center text-sm text-muted-foreground">No submissions to review</div>}
              {submissions.map((s) => {
                const checked = selectedSubIds.includes(s.id);
                return (
                  <div key={s.id} data-testid={`admin-bulk-row-${s.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-background p-3">
                    <Checkbox data-testid={`admin-bulk-checkbox-${s.id}`} checked={checked} onCheckedChange={(v) => setSelectedSubIds((prev) => v ? [...new Set([...prev, s.id])] : prev.filter((id) => id !== s.id))} />
                    <StatusBadge status={s.status}/>
                    <UserAvatar name={s.clipper_tag} src={s.clipper_avatar} size={32} testId={`admin-bulk-avatar-${s.id}`} />
                    <div className="min-w-0 flex-1"><div className="text-xs font-medium truncate">{s.clipper_tag || "Unknown"}</div><div className="text-[10px] text-muted-foreground truncate">{s.campaign_name} · {s.platform}</div></div>
                    <div className="text-right"><div className="text-xs font-semibold tabular-nums">{Number(s.views || 0).toLocaleString()} views</div></div>
                    <a href={s.video_url} target="_blank" rel="noreferrer" className="inline-flex h-7 items-center gap-1 rounded bg-[#c8ff00]/10 px-2 text-[10px] font-medium text-[#c8ff00] hover:bg-[#c8ff00]/20"><ExternalLink size={11}/> Open</a>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ── TEMPLATES ── */}
        <TabsContent value="templates" className="mt-4">
          <div data-testid="admin-templates-page" className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
            <Card className="border-border/60 bg-card/80"><CardHeader className="pb-2"><CardTitle className="text-sm">New Rejection Template</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="admin-template-title" value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} className="border-border/60 bg-background" placeholder="e.g. Old upload" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Primary Reason</Label><Input data-testid="admin-template-reason" value={tplReason} onChange={(e) => setTplReason(e.target.value)} className="border-border/60 bg-background" placeholder="Reason shown to moderators" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Message</Label><Textarea data-testid="admin-template-message" value={tplMessage} onChange={(e) => setTplMessage(e.target.value)} className="min-h-[110px] border-border/60 bg-background" placeholder="Reusable reviewer note" /></div>
              <Button data-testid="admin-template-save" className="w-full rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" disabled={!tplTitle.trim() || !tplReason.trim()} onClick={createTemplate}><Sparkles size={14}/> Save Template</Button>
            </CardContent></Card>
            <Card className="border-border/60 bg-card/80"><CardHeader className="pb-2 flex flex-row items-center justify-between"><CardTitle className="text-sm">Template Library</CardTitle><Button data-testid="admin-template-refresh" size="sm" variant="secondary" className="rounded-full" onClick={loadTemplates}><RefreshCw size={13}/></Button></CardHeader><CardContent className="space-y-2 max-h-[560px] overflow-y-auto">
              {templates.map((t) => (
                <div key={t.id} data-testid={`admin-template-card-${t.id}`} className="rounded-2xl border border-border/60 bg-background p-4">
                  <div className="flex flex-wrap items-center gap-2"><Badge className="bg-[#c8ff00]/10 text-[#c8ff00]">{t.primary_reason}</Badge><div className="text-sm font-bold">{t.title}</div><Button size="sm" variant="ghost" className="ml-auto h-7 w-7 p-0 text-rose-400" onClick={() => deleteTemplate(t.id)}><Trash2 size={12}/></Button></div>
                  <div className="mt-2 text-sm text-muted-foreground">{t.message || "No message"}</div>
                </div>
              ))}
              {!templates.length && <div data-testid="admin-template-empty" className="py-10 text-center text-sm text-muted-foreground">No templates saved yet.</div>}
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* ── VIDEO APPEALS ── */}
        <TabsContent value="appeals" className="mt-4">
          <Card data-testid="admin-appeals-page" className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-2">
              <Select value={appealStatus} onValueChange={setAppealStatus}><SelectTrigger data-testid="admin-appeals-status-filter" className="w-[140px] border-border/60 bg-background"><SelectValue/></SelectTrigger><SelectContent>{["pending","approved","denied"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
              <Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={loadAppeals}><RefreshCw size={12}/></Button>
              <div data-testid="admin-appeals-count" className="ml-auto text-xs text-muted-foreground">{appeals.length} {appealStatus}</div>
            </div>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {!appeals.length && <div data-testid="admin-appeals-empty" className="py-8 text-center text-sm text-muted-foreground">No {appealStatus} appeals</div>}
              {appeals.map((a) => (
                <div key={a.id} data-testid={`admin-appeal-card-${a.id}`} className="rounded-2xl border border-border/60 bg-background p-4 space-y-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <UserAvatar user={a.user} src={a.user?.avatar_url} size={40} testId={`admin-appeal-avatar-${a.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold">{a.user?.discord_username || a.user?.email || "Unknown user"}</div>
                      <div className="text-[10px] text-muted-foreground">{a.platform} · Submission {a.submission_id?.slice(0, 8)} · {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</div>
                      {a.video_url && <a href={a.video_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#c8ff00] hover:underline"><ExternalLink size={10}/> Open clip</a>}
                    </div>
                    <StatusBadge status={a.status}/>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-muted/20 p-3 text-xs whitespace-pre-wrap"><span className="font-semibold text-foreground">User reason:</span> {a.reason || "—"}</div>
                  {a.admin_response && <div className="rounded-xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.04] p-3 text-xs whitespace-pre-wrap"><span className="font-semibold text-foreground">Admin response:</span> {a.admin_response}</div>}
                  {a.status === "pending" && (
                    <div className="space-y-2">
                      <Textarea data-testid={`admin-appeal-response-${a.id}`} placeholder="Optional response sent to user..." value={appealResponse[a.id] || ""} onChange={(e) => setAppealResponse((prev) => ({ ...prev, [a.id]: e.target.value }))} className="min-h-[60px] border-border/60 bg-background text-xs" />
                      <div className="flex gap-2">
                        <Button data-testid={`admin-appeal-approve-${a.id}`} size="sm" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400" disabled={busyId === a.id} onClick={() => respondAppeal(a.id, "approved")}><CheckCircle2 size={13}/> Approve & restore clip</Button>
                        <Button data-testid={`admin-appeal-deny-${a.id}`} size="sm" variant="secondary" className="rounded-full bg-rose-500/15 text-rose-300 hover:bg-rose-500/25" disabled={busyId === a.id} onClick={() => respondAppeal(a.id, "denied")}><XCircle size={13}/> Deny</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ── BOTS ── */}
        <TabsContent value="bots" className="mt-4 space-y-4">
          {botInsights && (
            <Card data-testid="admin-bots-insights" className="border-border/60 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Insights</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-4">
                  <div className="rounded-xl border border-border/60 bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total rejections</div><div className="mt-1 text-2xl font-black">{botInsights.total_rejections || 0}</div></div>
                  <div className="rounded-xl border border-border/60 bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total appeals</div><div className="mt-1 text-2xl font-black">{botInsights.appeals?.total || 0}</div></div>
                  <div className="rounded-xl border border-border/60 bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Pending</div><div className="mt-1 text-2xl font-black text-amber-300">{botInsights.appeals?.pending || 0}</div></div>
                  <div data-testid="admin-bots-success-rate" className="rounded-xl border border-[#c8ff00]/30 bg-[#c8ff00]/[0.04] p-3"><div className="text-[10px] uppercase tracking-wide text-[#c8ff00]">Appeal success rate</div><div className="mt-1 text-2xl font-black text-[#c8ff00]">{Number(botInsights.appeals?.success_rate || 0).toFixed(1)}%</div><div className="text-[10px] text-muted-foreground">{botInsights.appeals?.approved || 0} approved · {botInsights.appeals?.denied || 0} denied</div></div>
                </div>
                <div>
                  <div className="mb-2 text-xs font-semibold text-foreground">Most common rejection reasons</div>
                  {botInsights.top_rejection_reasons?.length ? (
                    <div data-testid="admin-bots-top-reasons" className="space-y-1.5">
                      {botInsights.top_rejection_reasons.map((r) => {
                        const max = botInsights.top_rejection_reasons[0]?.count || 1;
                        const pct = Math.round((r.count / max) * 100);
                        return (
                          <div key={r.reason} className="rounded-lg border border-border/50 bg-background p-2">
                            <div className="flex items-center justify-between text-xs"><span className="font-medium">{r.reason}</span><span className="font-mono text-muted-foreground">{r.count}</span></div>
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted/40"><div className="h-full rounded-full bg-[#c8ff00]" style={{ width: `${pct}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">No rejection data yet.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <Card data-testid="admin-bots-page" className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold">Automated Moderation Bots</div>
              <Button variant="secondary" size="sm" className="ml-auto gap-1 rounded-full" onClick={loadBots}><RefreshCw size={12}/></Button>
            </div>
            <div className="text-xs text-muted-foreground">Toggle each bot on or off. When enabled, they will run automatically against new submissions.</div>
            <div className="space-y-3">
              {!bots.length && <div data-testid="admin-bots-empty" className="py-8 text-center text-sm text-muted-foreground">No bots configured</div>}
              {bots.map((b) => (
                <div key={b.id} data-testid={`admin-bot-card-${b.feature_key}`} className="flex flex-wrap items-start gap-4 rounded-2xl border border-border/60 bg-background p-4">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${b.enabled ? "bg-[#c8ff00]/15 text-[#c8ff00]" : "bg-muted text-muted-foreground"}`}><Bot size={18}/></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><div className="text-sm font-bold">{b.name}</div>{b.enabled && <Badge className="bg-[#c8ff00]/15 text-[#c8ff00]">Active</Badge>}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{b.description}</div>
                  </div>
                  <Switch data-testid={`admin-bot-toggle-${b.feature_key}`} checked={!!b.enabled} disabled={busyId === b.id} onCheckedChange={(v) => toggleBot(b.id, v)} />
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ── CONTACT ── */}
        <TabsContent value="contact" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-3"><div className="flex gap-2"><Button size="sm" variant={contactFilter==="forms"?"default":"secondary"} onClick={()=>setContactFilter("forms")}>Form Subs</Button><Button size="sm" variant={contactFilter==="bugs"?"default":"secondary"} onClick={()=>setContactFilter("bugs")}>Bug Reports</Button></div><div className="space-y-3 max-h-[560px] overflow-y-auto">
            {filteredContacts.length===0&&<div className="py-6 text-center text-sm text-muted-foreground">No messages</div>}
            {filteredContacts.map(c=>(<div key={c.id} data-testid={`contact-row-${c.id}`} className="rounded-2xl border border-border/60 bg-background p-5 space-y-2"><div className="flex items-start justify-between gap-2"><div><div className="flex items-center gap-2"><span className="text-sm font-semibold">{c.name}</span><StatusBadge status={c.status}/></div><div className="text-[11px] text-muted-foreground">{c.email} · {new Date(c.created_at).toLocaleString()}</div></div><div className="flex items-center gap-1 shrink-0">{c.status==="new"&&<Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-amber-400" onClick={()=>updateContactStatus(c.id,"read")} disabled={busyId===c.id}>Read</Button>}{c.status!=="resolved"&&<Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-emerald-400" onClick={()=>updateContactStatus(c.id,"resolved")} disabled={busyId===c.id}>Resolve</Button>}<Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-400" onClick={()=>deleteContact(c.id)} disabled={busyId===c.id}><Trash2 size={11}/></Button></div></div><div className="text-sm font-medium">{c.subject}</div><div className="text-sm text-muted-foreground whitespace-pre-wrap">{c.message}</div>{c.attachment_file_id && (<a data-testid={`contact-row-attachment-${c.id}`} href={`/api/files/${c.attachment_file_id}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-start gap-3 rounded-xl border border-border/60 bg-card/50 p-2 transition hover:border-[#c8ff00]/40"><img src={`/api/files/${c.attachment_file_id}`} alt="Bug report attachment" loading="lazy" className="h-24 w-24 rounded-lg object-cover" onError={(e) => { e.currentTarget.style.display='none'; }} /><div className="flex-1 self-center text-xs"><div className="font-semibold text-foreground">Attached screenshot</div><div className="text-muted-foreground">Click to open full size</div></div></a>)}</div>))}</div>
          </CardContent></Card>
        </TabsContent>

        {/* ── ANNOUNCEMENTS ── */}
        <TabsContent value="announcements" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-4">
            <div className="space-y-3">
              <div className="grid gap-2 md:grid-cols-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Type</Label>
                  <Select value={annKind} onValueChange={setAnnKind}>
                    <SelectTrigger data-testid="announcement-kind-select" className="border-border/60 bg-background"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User Notification</SelectItem>
                      <SelectItem value="global">Global Announcement</SelectItem>
                      <SelectItem value="campaign">Campaign Announcement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {annKind === "campaign" ? (
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Campaign</Label>
                    <Select value={annCampaignId} onValueChange={setAnnCampaignId}>
                      <SelectTrigger data-testid="announcement-campaign-select" className="border-border/60 bg-background"><SelectValue placeholder="Select which campaign this belongs to..." /></SelectTrigger>
                      <SelectContent>
                        {campaignsList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : annKind === "user" ? (
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Recipients</Label>
                    <div className="flex gap-2">
                      <Select value={annScope} onValueChange={setAnnScope}>
                        <SelectTrigger className="w-[180px] border-border/60 bg-background"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All clippers</SelectItem>
                          <SelectItem value="selected">Specific user</SelectItem>
                        </SelectContent>
                      </Select>
                      {annScope === "selected" && (
                        <Select onValueChange={(v) => setAnnUsers([v])}>
                          <SelectTrigger data-testid="announcement-user-select" className="border-border/60 bg-background"><SelectValue placeholder="Pick a user..." /></SelectTrigger>
                          <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.discord_username || u.email}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notification Tag</Label>
                    <Select value={annType} onValueChange={setAnnType}>
                      <SelectTrigger className="border-border/60 bg-background"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="campaign">Campaign</SelectItem>
                        <SelectItem value="clipper">Clipper</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Input data-testid="announcement-title-input" placeholder="Notification title" value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} className="border-border/60 bg-background text-sm" />
              <Textarea data-testid="announcement-message-input" placeholder="Notification message..." value={annMsg} onChange={(e) => setAnnMsg(e.target.value)} className="border-border/60 bg-background text-sm min-h-[80px]" />
              <Button
                data-testid="announcement-send-button"
                size="sm"
                className="gap-1 rounded-full bg-[#c8ff00] text-black font-bold hover:bg-[#b8ef00] disabled:opacity-50"
                onClick={sendAnnouncement}
                disabled={!annTitle || !annMsg || (annKind === "campaign" && !annCampaignId)}
              >
                <Send size={13} /> Send {annKind === "campaign" ? "campaign announcement" : annKind === "global" ? "global announcement" : "user notification"}
              </Button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">{announcements.map(a=>(<div key={a.id} className="flex items-start gap-3 rounded-xl border border-border/60 bg-background p-3"><div className="min-w-0 flex-1"><div className="text-xs font-semibold">{a.title}</div><div className="text-[10px] text-muted-foreground mt-0.5">{a.message}</div><div className="text-[9px] text-muted-foreground/60 mt-1">{new Date(a.created_at).toLocaleString()}</div></div><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-400 shrink-0" onClick={()=>deleteAnnouncement(a.id)} disabled={busyId===a.id}><Trash2 size={11}/></Button></div>))}</div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="issues" className="mt-4">
          <Card className="border-border/60 bg-card/80"><CardContent className="pt-5 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-semibold">Site Issues</div><div className="text-xs text-muted-foreground">Operational health — catch problems before users do.</div></div>
              <Button variant="secondary" size="sm" className="gap-1 rounded-full" onClick={loadSiteIssues}><RefreshCw size={12}/></Button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {siteIssues.map((m) => (
                <div key={m.key} data-testid={`site-issue-${m.key}`} className={[
                  "rounded-2xl border p-4",
                  m.severity === "high" ? "border-rose-500/30 bg-rose-500/[0.06]"
                    : m.severity === "medium" ? "border-amber-500/30 bg-amber-500/[0.06]"
                    : "border-border/60 bg-background",
                ].join(" ")}>
                  <div className={[
                    "text-2xl font-black tabular-nums",
                    m.count > 0 && m.severity === "high" ? "text-rose-400"
                      : m.count > 0 && m.severity === "medium" ? "text-amber-400"
                      : "text-foreground",
                  ].join(" ")}>{m.count}</div>
                  <div className="mt-1 text-[11px] font-medium text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
            {siteIssues.length === 0 && <div className="text-xs text-muted-foreground">No data yet — refresh to load.</div>}
          </CardContent></Card>
        </TabsContent>
          </div>
        </div>
      </Tabs>
      <Dialog open={Boolean(selectedUser)} onOpenChange={(open)=>!open&&setSelectedUser(null)}>
        <DialogContent data-testid="creator-insight-dialog" className="max-w-4xl max-h-[85vh] overflow-y-auto border-border bg-card text-card-foreground">
          <DialogHeader><DialogTitle>Creator Deep Insight</DialogTitle><DialogDescription>Review creator performance, status, risk, and internal notes.</DialogDescription></DialogHeader>
          {selectedUser && <div className="space-y-4"><div className="flex items-center gap-4"><UserAvatar user={selectedUser} size={64} /><div><div className="text-xl font-black">@{selectedUser.discord_username}</div><div className="text-sm text-muted-foreground">Status: {activityStatus(selectedUser).label} · Joined {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : "—"}</div></div></div>{userProfile ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[["Total Submissions", userProfile.total_submissions], ["Approved", userProfile.approved_count], ["Pending", userProfile.pending_count], ["Approval Rate", `${userProfile.approval_rate}%`], ["Total Earnings", `$${Number(userProfile.total_earned || 0).toFixed(2)}`], ["Avg Payout", `$${Number(userProfile.avg_payout || 0).toFixed(2)}`], ["Duplicate Rate", `${Number(userProfile.duplicate_rate || 0).toFixed(1)}%`], ["Risk Flag", userProfile.risk_flag], ["Last Active", userProfile.last_seen_at ? new Date(userProfile.last_seen_at).toLocaleString() : "—"]].map(([label,value])=><div key={label} className="rounded-xl border border-border/60 bg-background p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 text-lg font-bold">{value}</div></div>)}</div> : <div className="py-8 text-center text-sm text-muted-foreground">Loading profile...</div>}<div className="grid gap-3 md:grid-cols-2"><div className="rounded-xl border border-border/60 bg-background p-4"><div className="mb-2 text-sm font-semibold">Common Rejection Reasons</div>{(userProfile?.rejection_reasons || ["No rejection history"]).map((r)=><Badge key={r} className="mr-1 mt-1 bg-rose-500/10 text-rose-300">{r}</Badge>)}</div><div className="rounded-xl border border-border/60 bg-background p-4"><div className="mb-2 text-sm font-semibold">Admin Notes</div><Textarea placeholder="Internal notes, warnings, escalation flags..." className="min-h-[110px]" /></div></div><div className="grid gap-3 xl:grid-cols-2"><div className="rounded-xl border border-border/60 bg-background p-4"><div className="mb-3 text-sm font-semibold">Joined Campaigns Performance</div>{userProfile?.campaign_stats?.length ? <div className="max-h-[220px] space-y-2 overflow-y-auto">{userProfile.campaign_stats.map((c)=><div key={c.campaign_id} className="rounded-lg border border-border/50 p-3 text-xs"><div className="font-bold">{c.name}</div><div className="mt-2 grid grid-cols-4 gap-2 text-center"><div><div className="font-bold">{Number(c.views||0).toLocaleString()}</div><div className="text-muted-foreground">Views</div></div><div><div className="font-bold text-emerald-400">{c.approved}</div><div className="text-muted-foreground">Approved</div></div><div><div className="font-bold text-rose-400">{c.rejected}</div><div className="text-muted-foreground">Rejected</div></div><div><div className="font-bold text-amber-400">{c.pending}</div><div className="text-muted-foreground">Pending</div></div></div></div>)}</div> : <div className="text-sm text-muted-foreground">No joined campaign stats yet</div>}</div><div className="rounded-xl border border-border/60 bg-background p-4"><div className="mb-3 text-sm font-semibold">Wallet & Payouts</div><div className="grid grid-cols-2 gap-2 text-xs"><div className="rounded-lg bg-muted/30 p-2"><div className="text-muted-foreground">Available</div><div className="font-bold text-[#c8ff00]">${Number(userProfile?.wallet?.available_balance || 0).toFixed(2)}</div></div><div className="rounded-lg bg-muted/30 p-2"><div className="text-muted-foreground">Pending</div><div className="font-bold">${Number(userProfile?.wallet?.pending_payout || 0).toFixed(2)}</div></div><div className="rounded-lg bg-muted/30 p-2"><div className="text-muted-foreground">On Hold / Paused</div><div className="font-bold">${Number(userProfile?.wallet?.on_hold_balance || 0).toFixed(2)}</div></div><div className="rounded-lg bg-muted/30 p-2"><div className="text-muted-foreground">All Time</div><div className="font-bold">${Number(userProfile?.total_earned || 0).toFixed(2)}</div></div></div><div className="mt-3 max-h-[130px] space-y-1 overflow-y-auto">{userProfile?.payout_history?.length ? userProfile.payout_history.map((p)=><div key={p.id} className="flex justify-between rounded-lg border border-border/50 px-3 py-2 text-xs"><span className="capitalize">{p.method}</span><span>${Number(p.amount_paid || p.amount || 0).toFixed(2)} · {p.status}</span></div>) : <div className="text-xs text-muted-foreground">No payout history</div>}</div></div></div><div className="rounded-xl border border-border/60 bg-background p-4"><div className="mb-3 text-sm font-semibold">Connected Socials</div>{userProfile?.socials?.length ? <div className="flex flex-wrap gap-2">{userProfile.socials.map((s)=><Badge key={s.id} className="bg-[#c8ff00]/10 text-[#c8ff00]">{s.platform} @{s.handle}</Badge>)}</div> : <div className="text-sm text-muted-foreground">No connected socials</div>}</div><div className="rounded-xl border border-border/60 bg-background p-4"><div className="mb-3 text-sm font-semibold">Moderation History</div>{userProfile?.moderation_history?.length ? <div className="max-h-[180px] space-y-2 overflow-y-auto">{userProfile.moderation_history.map((h)=><div key={h.id} className="rounded-lg border border-border/50 px-3 py-2 text-xs"><div className="flex items-center justify-between"><span className="capitalize font-medium">{String(h.action || "Action").replace(/_/g," ")}</span><span className="text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleString() : "—"}</span></div>{(h.reason || h.template_title) && <div data-testid={`moderation-reason-${h.id}`} className="mt-1 text-rose-300">{h.reason || h.template_title}</div>}</div>)}</div> : <div className="text-sm text-muted-foreground">No moderation history yet</div>}</div>{userProfile?.duplicate_submissions?.length > 0 && <div data-testid="duplicate-clips-section" className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4"><div className="mb-3 text-sm font-semibold text-amber-300">Duplicate Clips Detected ({userProfile.duplicate_submissions.length})</div><div className="max-h-[180px] space-y-2 overflow-y-auto">{userProfile.duplicate_submissions.map((d)=><div key={d.id} data-testid={`duplicate-clip-${d.id}`} className="rounded-lg border border-amber-500/30 bg-background px-3 py-2 text-xs"><div className="flex items-center justify-between"><span className="capitalize font-medium">{d.platform || "—"} · {d.status}</span><a href={d.video_url} target="_blank" rel="noreferrer" className="text-[#c8ff00] hover:underline">Open</a></div>{d.external_video_id && <div className="mt-1 text-muted-foreground truncate">ID: {d.external_video_id}</div>}</div>)}</div></div>}
            {selectedUser && (
              <div data-testid="user-insights-access-section" className="rounded-xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.03] p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-foreground">Campaign Insights Access</div>
                    <div className="text-xs text-muted-foreground">Check a campaign to let this user view its analytics on the Insights page.</div>
                  </div>
                  <Badge data-testid="user-insights-access-count" className="bg-[#c8ff00]/15 text-[#c8ff00]">
                    {(userInsightAssignments[selectedUser.id] || []).length} assigned
                  </Badge>
                </div>
                <div className="grid max-h-[220px] gap-1 overflow-y-auto sm:grid-cols-2">
                  {(campaignsList || []).map((c) => {
                    const assigned = (userInsightAssignments[selectedUser.id] || []).includes(c.id);
                    return (
                      <label
                        key={c.id}
                        data-testid={`insight-access-row-${c.id}`}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${
                          assigned ? "border-[#c8ff00]/40 bg-[#c8ff00]/[0.08]" : "border-border/60 bg-background hover:border-[#c8ff00]/30"
                        } ${insightBusyUser === selectedUser.id ? "pointer-events-none opacity-60" : ""}`}
                      >
                        <Checkbox
                          data-testid={`insight-access-toggle-${c.id}`}
                          checked={assigned}
                          onCheckedChange={() => toggleUserInsightCampaign(selectedUser.id, c.id)}
                          className="border-[#c8ff00]/60 data-[state=checked]:bg-[#c8ff00] data-[state=checked]:text-black"
                        />
                        <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                        <span className="text-[10px] capitalize text-muted-foreground">{c.status}</span>
                      </label>
                    );
                  })}
                  {(!campaignsList || !campaignsList.length) && (
                    <div className="text-xs text-muted-foreground">No campaigns yet.</div>
                  )}
                </div>
              </div>
            )}
            {userProfile?.campaign_earnings?.length > 0 && (
              <div data-testid="user-campaign-earnings-section" className="rounded-xl border border-border/60 bg-background p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-foreground">Campaign Earnings ({userProfile.campaign_earnings.length})</div>
                  <div className="text-xs text-muted-foreground">Per-campaign eligibility & minimum thresholds</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border/60 text-left text-muted-foreground">
                        <th className="px-2 py-2 text-[10px] font-medium uppercase tracking-widest">Campaign</th>
                        <th className="px-2 py-2 text-right text-[10px] font-medium uppercase tracking-widest">Views</th>
                        <th className="px-2 py-2 text-right text-[10px] font-medium uppercase tracking-widest">Earnings</th>
                        <th className="px-2 py-2 text-right text-[10px] font-medium uppercase tracking-widest">Minimum</th>
                        <th className="px-2 py-2 text-[10px] font-medium uppercase tracking-widest">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userProfile.campaign_earnings.map((c) => (
                        <tr key={c.campaign_id} data-testid={`user-campaign-earning-${c.campaign_id}`} className="border-b border-border/40 last:border-0">
                          <td className="px-2 py-2 font-semibold text-foreground">{c.campaign_name}</td>
                          <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{Number(c.views).toLocaleString()}</td>
                          <td className={`px-2 py-2 text-right font-bold tabular-nums ${c.below_threshold ? "text-muted-foreground line-through" : "text-[#c8ff00]"}`}>${Number(c.earnings).toFixed(2)}</td>
                          <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">${Number(c.minimum).toFixed(2)}</td>
                          <td className="px-2 py-2">
                            <span className={[
                              "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                              c.below_threshold ? "bg-rose-500/15 text-rose-300"
                                : c.eligible ? "bg-emerald-500/15 text-emerald-300"
                                : "bg-amber-500/15 text-amber-300",
                            ].join(" ")}>
                              {c.below_threshold ? "Below Minimum" : c.eligible ? "Eligible" : "Not Eligible"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {userProfile?.video_earnings?.length > 0 && (
              <div data-testid="video-earnings-section" className="rounded-xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.03] p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-foreground">Video Earnings ({userProfile.video_earnings.length})</div>
                  <div className="text-xs text-muted-foreground">Sorted by highest earnings</div>
                </div>
                <div className="max-h-[260px] space-y-2 overflow-y-auto">
                  {userProfile.video_earnings.map((v) => (
                    <div key={v.id} data-testid={`video-earning-${v.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-border/50 bg-background px-3 py-2 text-xs">
                      <StatusBadge status={v.status} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-foreground">{v.campaign_name}</div>
                        <div className="text-[10px] text-muted-foreground capitalize">{v.platform} · {v.created_at ? new Date(v.created_at).toLocaleDateString() : "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold tabular-nums">{Number(v.views || 0).toLocaleString()} views</div>
                        <div className="font-black text-[#c8ff00]">${Number(v.earned || 0).toFixed(2)}</div>
                      </div>
                      {v.video_url && (
                        <a data-testid={`video-earning-open-${v.id}`} href={v.video_url} target="_blank" rel="noreferrer" className="inline-flex h-7 items-center gap-1 rounded bg-[#c8ff00]/10 px-2 text-[10px] font-medium text-[#c8ff00] hover:bg-[#c8ff00]/20">
                          <ExternalLink size={10}/> Open
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}</div>}
        </DialogContent>
      </Dialog>

      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent data-testid="role-create-dialog" className="max-w-xl border-border bg-card text-card-foreground">
          <DialogHeader>
            <DialogTitle>Create Role</DialogTitle>
            <DialogDescription>Define a new role with permissions and page access.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Role Name</label>
              <Input data-testid="role-create-name" value={newRoleDraft.name} onChange={(e) => setNewRoleDraft({ ...newRoleDraft, name: e.target.value })} className="border-border/60 bg-background" />
            </div>
            <div className="grid gap-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Description</label>
              <Textarea data-testid="role-create-description" value={newRoleDraft.description} onChange={(e) => setNewRoleDraft({ ...newRoleDraft, description: e.target.value })} className="border-border/60 bg-background" />
            </div>
            <div className="grid gap-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Status</label>
              <Select value={newRoleDraft.status} onValueChange={(value) => setNewRoleDraft({ ...newRoleDraft, status: value })}>
                <SelectTrigger data-testid="role-create-status" className="border-border/60 bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</label>
              <Textarea data-testid="role-create-notes" value={newRoleDraft.notes} onChange={(e) => setNewRoleDraft({ ...newRoleDraft, notes: e.target.value })} className="border-border/60 bg-background" />
            </div>
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Permissions</div>
              <div className="flex flex-wrap gap-2">
                {PERMISSION_OPTIONS.map((perm) => (
                  <Button
                    key={perm}
                    data-testid={`role-create-permission-${perm}`}
                    type="button"
                    variant="secondary"
                    size="sm"
                    className={(newRoleDraft.permissions || []).includes(perm) ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full"}
                    onClick={() => {
                      const perms = newRoleDraft.permissions || [];
                      setNewRoleDraft({ ...newRoleDraft, permissions: perms.includes(perm) ? perms.filter((p) => p !== perm) : [...perms, perm] });
                    }}
                  >
                    {perm.replace(/_/g, " " )}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Assigned Pages</div>
              <div className="flex flex-wrap gap-2">
                {PAGE_PERMISSION_OPTIONS.map((page) => (
                  <Button
                    key={page}
                    data-testid={`role-create-page-${page}`}
                    type="button"
                    variant="secondary"
                    size="sm"
                    className={(newRoleDraft.pages || []).includes(page) ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full"}
                    onClick={() => {
                      const pages = newRoleDraft.pages || [];
                      setNewRoleDraft({ ...newRoleDraft, pages: pages.includes(page) ? pages.filter((p) => p !== page) : [...pages, page] });
                    }}
                  >
                    {page.replace(/_/g, " " )}
                  </Button>
                ))}
              </div>
            </div>
            <Button data-testid="role-create-submit" className="w-full rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" onClick={createRole} disabled={!newRoleDraft.name.trim()}>
              Create Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RejectClipDialog
        open={Boolean(rejectTarget)}
        onOpenChange={(o) => { if (!o) { setRejectTarget(null); setPendingBulkAfterReject(false); } }}
        submission={rejectTarget}
        onConfirm={confirmReject}
      />

      <AppealThreadDialog
        open={Boolean(appealThread)}
        onOpenChange={(o) => { if (!o) setAppealThread(null); }}
        submission={appealThread?.submission}
        appeal={appealThread?.appeal}
        adminMode
        onResolved={() => { setAppealThread(null); loadSubmissions(); loadAppeals?.(); }}
      />

    </div>
  );
}
